import React from 'react';
import objectPath from 'object-path';
import { observer, inject } from 'mobx-react';
import Alert from 'react-s-alert';
import styled from 'styled-components';
import { withNamespaces, Trans } from 'react-i18next';
import PropTypes from 'prop-types';
import { Link } from 'core/routing';
import {
  CloseTaskEmptyIcon,
  DriveEmptyIcon,
  HistoryEmptyIcon,
  OpenTaskEmptyIcon,
  ProjectEmptyIcon,
  StoryEmptyIcon,
} from 'icons';

import { Center } from 'legacyComponents/styled/lib';
import {
  Button,
  Icon,
  Heading,
  EmptyWrapper,
  EmptyText,
  EmptyDescription,
  VisibilityWrapper,
} from 'components';
import { media } from 'legacyComponents/styled/styleUtils';
import { conf } from 'legacyComponents/cards/config.yaml';

import { OperateChecker } from 'apps/components';
import { modalStore } from 'apps/modals/app';
import { clientStore } from 'apps/client/app';
import { PostForm } from 'apps/aggregator/postForm/index';
import { env } from 'process';

const Card = styled.div`
  height: auto;
  margin: 0 auto ${props => props.theme.spacing.large};
  background-color: ${props => props.theme.color.wfGrey100};
  padding: ${props => (props.small ? '40px' : '80px 40px')};
`;

const NoDataIcon = styled.div`
  color: ${props => props.theme.color.wfBlack};
  border-radius: 50%;
  transition: left 250ms ease-in-out;
  margin: 0 auto;
  text-align: center;
`;

const HeadingStyled = styled(Heading)`
  text-align: center;
  margin: ${props => props.theme.spacing.default};
`;

const ButtonStyled = styled(Button)`
  margin: 0 ${props => props.theme.spacing.small};
`;

const TextWrapper = styled.div`
  width: 620px;
  display: block;
  margin: 0 auto;
  ${media.s`
    width: 95%;
  `};
`;

const Text = styled.p`
  text-align: center;
  line-height: 1.6;
  margin: ${props => (props.margin ? '6px 0px' : '0px')};
  ${media.s`
    width: 100%;
    margin: ${props => (props.margin ? '12px 0 20px 0' : '0px')};
  `};
`;

@withNamespaces()
@inject('routerStore')
@observer
export class NoDataCard extends React.Component<Record<string, any>> {
  writePost = () => {
    modalStore.SetModalComponent(PostForm);
  };

  openIssue = () => {
    this.props.manager.OpenIssueForm();
  };

  createReadme = () => {
    this.props.manager.CreateReadme();
  };

  openReadmeTemplates = () => {
    this.props.manager.OpenReadmeTemplates();
  };

  thanksMessage = () => {
    Alert.success('You think? Thanks, that means a lot!');
  };

  welcomeMessage = () => {
    Alert.success("You're welcome.");
  };

  addIndustries = () => {
    this.props.manager.SetEditingIndustries(true);
  };

  addCertificates = () => {
    this.props.manager.SetEditingCertificates(true);
  };

  addServices = () => {
    const { routerStore } = this.props;
    const { route } = routerStore;
    routerStore.navigate('initiative.services.editFabrication', route.params);
  };

  uploadFile = () => {
    const { routerStore } = this.props;
    const { route } = routerStore;
    routerStore.navigate('workspace.share-file', route.params);
  };

  render() {
    const {
      type,
      owner,
      ownerName,
      isChannel, // TODO Remove isChannel, isCollection, isWhitelabel when no parent component is using them
      isCollection,
      isWhitelabel,
      spaceType,
      t,
      routerStore,
      hideButtons,
      manager,
      project,
    } = this.props;
    let lookUp;
    switch (spaceType) {
      case 'channel':
        lookUp = 'channelSpace';
        break;
      case 'collection':
        lookUp = 'collectionSpace';
        break;
      case 'whitelabel':
        lookUp = 'whitelabelSpace';
        break;
      case 'discover':
        lookUp = 'discoverSpace';
        break;
      default:
        /* TODO This block is here to ensure backwards compatibility.
            Once we refactor every noData usage to use spaceType instead of
            isChannel, isCollection or isWhitelabel we can leave only the
            owner prop check */
        lookUp = isChannel
          ? 'channelSpace'
          : isCollection
          ? 'collectionSpace'
          : isWhitelabel
          ? 'whitelabelSpace'
          : owner
          ? 'spaceOwners'
          : 'spaceViewers';
        break;
    }

    const buttons: JSX.Element[] = [];
    const noDataDefault = {
      title: 'No data to show.',
      text: "There's no data available for this section",
      buttons: null as any,
    };
    const typeConf = objectPath.get(
      conf,
      [lookUp, type].join('.'),
      noDataDefault,
    );
    if (typeConf.buttons && hideButtons !== true) {
      const btnProps = Object.keys(typeConf.buttons);

      btnProps.forEach(btn => {
        const button = typeConf.buttons[btn];
        let tailoredButton;

        // Create the button element, depending on the onClick existance
        if (button.onClick) {
          tailoredButton = (
            <OperateChecker key={btn} type='canCreateIn'>
              <ButtonStyled
                text={button.title}
                size='default'
                icon={button.icon && button.icon}
                iconPosition='right'
                onClick={this[button.onClick]}
                key={button.title}
                outline
                data-testid={button.dataTestId}
              />
            </OperateChecker>
          );
        } else {
          tailoredButton = (
            <OperateChecker key={btn} type='canCreateIn'>
              <Link name={button.link} key={button.title}>
                <ButtonStyled
                  size='default'
                  icon={button.icon && button.icon}
                  iconPosition='right'
                  text={button.title}
                  outline
                  data-testid={button.dataTestId}
                />
              </Link>
            </OperateChecker>
          );
        }

        /* Here, we "clear" that button for a specific form type, and a specific
           title.
           TODO: Remove this condition and restore back the old method
           once the templates have been fully deployed
        */

        if (env.MODE === 'production') {
          if (type === 'readme' && button.title === 'Select template') {
            tailoredButton = undefined;
          }
        } else if (routerStore.route.path.split('/')[1] !== '+opennext') {
          if (type === 'readme' && button.title === 'Select template') {
            tailoredButton = undefined;
          }
        }

        // Only show the button if it passes the filter
        if (tailoredButton) {
          buttons.push(tailoredButton);
        }
      });
    }
    const appendName = [
      'project',
      'collection',
      'story',
      'industries',
      'certificates',
      'fabServices',
    ];
    const thinnerView = ['post', 'project', 'collection', 'story'];
    let cardText = t(typeConf.text);
    // FIXME
    // eslint-disable-next-line
    if (!owner && appendName.includes(type)) {
      cardText = t(`{{ownerName}} ${typeConf.text}`, { ownerName });
    }
    const iconName = conf.icons[type];

    const atHead = manager?.atHead; // manager is repoManager
    const canContribute =
      project?.inSpace.whichTypes.includes('Contribution') && atHead;
    const { isStaff, signedInProfile } = clientStore;

    const canAccessStory = isStaff && signedInProfile;

    const iconMap: Record<string, any> = {
      story: (
        <EmptyWrapper className=' bg-white border border-wfGrey100 py-16 -mt-3 shadow-sm'>
          <StoryEmptyIcon />
          <EmptyText text='No Stories to show' />
          <EmptyDescription
            className='mb-4'
            text={
              canAccessStory
                ? 'Stories you have published and saved to your drafts will be displayed here.'
                : null
            }
          />
          <VisibilityWrapper visible={canAccessStory}>
            <OperateChecker type='canCreateIn'>
              <Button
                outline
                data-testid='CommunityProfilePage-TellStory-button'
                onClick={() => routerStore.navigate('home.newStory')}
                text='Tell a Story'
              />
            </OperateChecker>
          </VisibilityWrapper>
        </EmptyWrapper>
      ),
      readme: (
        <EmptyWrapper className=' bg-white border border-wfGrey100 py-16 -mt-3 shadow-sm'>
          <StoryEmptyIcon />
          <EmptyText text='Document your project' />
          <EmptyDescription
            className='mb-4'
            text='Help others understand, replicate and contribute to your project.'
          />
          {canContribute && (
            <Button
              outline
              data-testid='create-readme-button'
              onClick={() => this.createReadme()}
              text='Start Writing'
            />
          )}
        </EmptyWrapper>
      ),
      repo: (
        <EmptyWrapper className=' bg-white'>
          <DriveEmptyIcon />
          <EmptyText text='Upload files or folders to get started.' />
          <EmptyDescription
            className='mb-2'
            text='Contribute your local changes for your collaborators to see. Once contributed you can open 3d files in your collaborative CAD tool.'
          />
        </EmptyWrapper>
      ),
      contributions: (
        <EmptyWrapper className=' bg-white'>
          <HistoryEmptyIcon />
          <EmptyText text='No Contributions to show' />
          <EmptyDescription
            className='mb-2'
            text='Start your your first contribution by clicking the upload icon on the top right corner of the Drive section above.'
          />
        </EmptyWrapper>
      ),
      openIssues: (
        <EmptyWrapper className=' bg-white'>
          <OpenTaskEmptyIcon />
          <EmptyText text='No Open Tasks' />
          <EmptyDescription
            className='mb-2 text-center inline-block'
            text="There's a lot to learn from past tasks. That's why they will always be a click away."
          />
        </EmptyWrapper>
      ),
      closedIssues: (
        <EmptyWrapper className=' bg-white'>
          <CloseTaskEmptyIcon />
          <EmptyText text='No Closed Tasks' />
          <EmptyDescription
            className='mb-2 text-center inline-block'
            text="There's a lot to learn from past tasks. That's why they will always be a click away."
          />
        </EmptyWrapper>
      ),
      project: (
        <EmptyWrapper className='bg-white border border-wfGrey100 py-16 -mt-3 shadow-sm'>
          <ProjectEmptyIcon />
          <EmptyText text='No Projects to show' />
          <EmptyDescription
            className='mb-2'
            text='Projects you have created, forked and/or contributed to will be displayed here.'
          />
          <OperateChecker type='canCreateIn'>
            <div className='flex'>
              <Button
                outline
                text='Start a Project'
                onClick={() => {
                  routerStore.navigate('home.newProject');
                }}
                dataTestId='CommunityProfilePage-StartProject-Button'
              />
              <Button
                outline
                text='Discover Projects'
                onClick={() => {
                  routerStore.navigate('home');
                }}
                style={{
                  marginLeft: 16,
                }}
                dataTestId='CommunityProfilePage-DiscoverProject-Button'
              />
            </div>
          </OperateChecker>
        </EmptyWrapper>
      ),
      collection: (
        <EmptyWrapper className='bg-white border border-wfGrey100 py-16 -mt-3 shadow-sm'>
          <StoryEmptyIcon />
          <EmptyText text='No Collections to show' />
          <EmptyDescription
            className='mb-2'
            text="Projects that you've starred will be displayed in Collections here."
          />

          <Button
            outline
            text='Discover Collections'
            onClick={() => {
              routerStore.navigate('home');
            }}
            dataTestId='CommunityProfilePage-DiscoverCollections-Button'
          />
        </EmptyWrapper>
      ),
    };

    return (
      iconMap[type] || (
        <Card small={thinnerView.includes(type) && !owner}>
          {thinnerView.includes(type) && (isChannel || !owner) ? (
            ''
          ) : (
            <>
              <NoDataIcon>
                {iconName && <Icon name={iconName} size='xlarge' />}
              </NoDataIcon>
              <HeadingStyled
                style={{ marginBottom: 0 }}
                level={4}
                size='medium'
              >
                <Trans>{typeConf.title}</Trans>
              </HeadingStyled>
            </>
          )}
          <TextWrapper>
            <Text margin={!thinnerView.includes(type) || owner}>
              {cardText}
            </Text>
          </TextWrapper>
          {['s', 'xs'].includes(clientStore.activeBreakPoint) ? (
            ''
          ) : (
            <Center>{buttons}</Center>
          )}
        </Card>
      )
    );
  }
}

NoDataCard.propTypes = {
  type: PropTypes.string,
  owner: PropTypes.bool,
  ownerName: PropTypes.string,
  manager: PropTypes.object,
  isChannel: PropTypes.bool,
  isCollection: PropTypes.bool,
  isWhitelabel: PropTypes.bool,
  routerStore: PropTypes.object,
  spaceType: PropTypes.string,
  t: PropTypes.func,
  hideButtons: PropTypes.bool,
};
